import { useId, type ReactNode } from 'react';
import Box, { type BoxProps } from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { color, cssEase, font, glass, radius } from './tokens';

/**
 * Four L-shaped brackets on the corners of a box: the "instrument panel" detail from the reference.
 * Each bracket is a corner mark, not an edge border.
 */
export function Corners({ size = 10, tone = color.lineStrong, inset = -1 }: { size?: number; tone?: string; inset?: number }) {
    const base = { position: 'absolute', width: size, height: size, borderColor: tone, borderStyle: 'solid', pointerEvents: 'none', transition: `border-color .3s ${cssEase.snap}` } as const;
    return (
        <>
            <Box className="cs-corner" sx={{ ...base, top: inset, left: inset, borderWidth: '1px 0 0 1px' }} />
            <Box className="cs-corner" sx={{ ...base, top: inset, right: inset, borderWidth: '1px 1px 0 0' }} />
            <Box className="cs-corner" sx={{ ...base, bottom: inset, left: inset, borderWidth: '0 0 1px 1px' }} />
            <Box className="cs-corner" sx={{ ...base, bottom: inset, right: inset, borderWidth: '0 1px 1px 0' }} />
        </>
    );
}

interface GlassPanelProps extends BoxProps {
    children?: ReactNode;
    /** Show corner brackets. */
    corners?: boolean;
    /** Lift and light up on hover (for clickable cards). */
    interactive?: boolean;
}

/** Frosted glass surface used for cards, toolbars and panels. */
export function GlassPanel({ children, corners = false, interactive = false, sx, ...rest }: GlassPanelProps) {
    return (
        <Box
            {...rest}
            sx={[
                {
                    position: 'relative',
                    ...glass,
                    borderRadius: `${radius.sm}px`,
                    transition: `background-color .35s ${cssEase.snap}, border-color .35s ${cssEase.snap}, transform .35s ${cssEase.snap}, box-shadow .35s ${cssEase.snap}`,
                },
                interactive && {
                    cursor: 'pointer',
                    '&:hover': {
                        backgroundColor: color.panelHover,
                        borderColor: color.lineStrong,
                        transform: 'translateY(-2px)',
                        boxShadow: `0 18px 48px -24px ${color.signal}66`,
                        '& .cs-corner': { borderColor: color.signal },
                    },
                },
                ...(Array.isArray(sx) ? sx : [sx]),
            ]}
        >
            {corners && <Corners />}
            {children}
        </Box>
    );
}

/** Small uppercase mono label with a spark dot, used above headings and on chips. */
export function Kicker({ children, tone = color.spark, sx }: { children: ReactNode; tone?: string; sx?: BoxProps['sx'] }) {
    return (
        <Typography
            variant="overline"
            component="div"
            sx={[
                { display: 'inline-flex', alignItems: 'center', gap: 1, color: color.textMuted, textTransform: 'uppercase', fontFamily: font.mono },
                ...(Array.isArray(sx) ? sx : [sx]),
            ]}
        >
            <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: tone, boxShadow: `0 0 10px ${tone}` }} />
            {children}
        </Typography>
    );
}

/** The CoSparks mark: a four-point spark inside a thin ring. */
export function SparkMark({ size = 28, animated = false }: { size?: number; animated?: boolean }) {
    // Unique per instance: a shared id would resolve to the first copy, which may sit in a hidden subtree
    const gradientId = `cs-spark-grad-${useId().replace(/:/g, '')}`;
    return (
        <Box
            component="svg"
            viewBox="0 0 32 32"
            width={size}
            height={size}
            aria-hidden
            sx={animated ? {
                '@keyframes cs-spark-pulse': { '0%, 100%': { transform: 'scale(1) rotate(0deg)' }, '50%': { transform: 'scale(0.86) rotate(45deg)' } },
                '& .cs-spark-core': { transformOrigin: '16px 16px', animation: 'cs-spark-pulse 6s ease-in-out infinite' },
            } : undefined}
        >
            <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0" stopColor="#ffd27a" />
                    <stop offset="0.45" stopColor={color.spark} />
                    <stop offset="1" stopColor={color.signal} />
                </linearGradient>
            </defs>
            <circle cx="16" cy="16" r="14.5" fill="none" stroke={color.lineStrong} strokeWidth="1" />
            <path
                className="cs-spark-core"
                d="M16 4 C17 12 20 15 28 16 C20 17 17 20 16 28 C15 20 12 17 4 16 C12 15 15 12 16 4 Z"
                fill={`url(#${gradientId})`}
            />
        </Box>
    );
}

export function Wordmark({ size = 20 }: { size?: number }) {
    return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.25 }}>
            <SparkMark size={size + 8} animated />
            <Typography component="span" sx={{ fontFamily: font.display, fontWeight: 600, fontSize: size, letterSpacing: '-0.03em', color: color.text }}>
                CoSparks
            </Typography>
        </Box>
    );
}
